import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { confirmDelete } from '../utils/confirmDelete';
import ModalPortal from '../components/ModalPortal';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { Mail, Plus, Search, Edit, Trash2, Copy, FileText, Code, Sparkles, X, Eye, Save } from 'lucide-react';
import { EmailTemplatesSkeleton } from '../components/Skeleton';

const EmailTemplates = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [createType, setCreateType] = useState('text'); // text, html, ai
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    prompt: '',
    template_type: 'text'
  });
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [fixingTemplateTypes, setFixingTemplateTypes] = useState(false);

  // Fetch templates using React Query
  const { data: templatesData, isLoading: loading } = useQuery(
    ['emailTemplates', currentWorkspace?.id, searchTerm],
    async () => {
      if (!currentWorkspace?.id) {
        return { templates: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/email-templates', {
        params: { search: searchTerm || undefined },
        headers
      });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to fetch email templates');
      }
    }
  );

  const templates = templatesData?.templates || [];

  const handleNewTemplate = () => {
    navigate('/template-creator');
  };

  // Fix template types utility
  const handleFixTemplateTypes = async () => {
    if (!window.confirm('This will automatically detect and fix template types for all your templates. Continue?')) {
      return;
    }

    try {
      setFixingTemplateTypes(true);
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post('/api/email-templates/fix-template-types', {}, { headers });

      if (response.data.updatedCount > 0) {
        toast.success(`Fixed template types for ${response.data.updatedCount} templates!`);
        queryClient.invalidateQueries(['emailTemplates', currentWorkspace?.id]);
      } else {
        toast.success('All template types are already correct!');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to fix template types');
    } finally {
      setFixingTemplateTypes(false);
    }
  };

  // Quick template creation functions
  const quickTemplates = [
    {
      name: 'Welcome Email',
      type: 'text',
      prompt: 'Create a professional welcome email for new customers',
      icon: '👋'
    },
    {
      name: 'Promotional Email',
      type: 'text',
      prompt: 'Create an engaging promotional email with discount offer',
      icon: '🎉'
    },
    {
      name: 'Newsletter',
      type: 'text',
      prompt: 'Create a monthly newsletter template with company updates',
      icon: '📰'
    },
    {
      name: 'Thank You Email',
      type: 'text',
      prompt: 'Create a heartfelt thank you email for customer support',
      icon: '🙏'
    },
    {
      name: 'Product Launch',
      type: 'html',
      prompt: 'Create an exciting HTML email for product launch announcement',
      icon: '🚀'
    },
    {
      name: 'Event Invitation',
      type: 'html',
      prompt: 'Create an elegant HTML invitation email for company event',
      icon: '🎪'
    }
  ];

  const handleQuickTemplate = (template) => {
    // Navigate to template creator with AI type and pre-filled prompt
    navigate('/template-creator', {
      state: {
        templateType: 'ai',
        templateData: {
          name: template.name,
          prompt: template.prompt,
          template_type: template.type
        }
      }
    });
  };

  // Bulk operations functions
  const handleSelectTemplate = (templateId) => {
    setSelectedTemplates(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTemplates.length === templates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(templates.map(template => template.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedTemplates.length === 0) return;

    if (confirmDelete(`Are you sure you want to delete ${selectedTemplates.length} template(s)?`)) {
      selectedTemplates.forEach(templateId => {
        deleteTemplateMutation.mutate(templateId);
      });
      setSelectedTemplates([]);
    }
  };


  // Create template mutation
  const createTemplateMutation = useMutation(
    async (templateData) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      let endpoint = '';
      let payload = {};

      switch (createType) {
        case 'text':
          endpoint = '/api/email-templates/create-text';
          payload = {
            name: templateData.name,
            subject: templateData.subject,
            content: templateData.content
          };
          break;
        case 'html':
          endpoint = '/api/email-templates/create-html';
          payload = {
            name: templateData.name,
            subject: templateData.subject,
            html_content: templateData.content
          };
          break;
        case 'ai':
          endpoint = '/api/email-templates/create-ai';
          payload = {
            name: templateData.name,
            prompt: templateData.prompt,
            template_type: templateData.template_type
          };
          break;
        default:
          throw new Error('Invalid template type');
      }

      const response = await apiClient.post(endpoint, payload, { headers });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Email template created successfully!');
        setShowCreateModal(false);
        setFormData({
          name: '',
          subject: '',
          content: '',
          prompt: '',
          template_type: 'text'
        });
        queryClient.invalidateQueries(['emailTemplates', currentWorkspace?.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create email template');
      }
    }
  );

  const handleCreateTemplate = () => {
    createTemplateMutation.mutate(formData);
  };

  // Delete template mutation
  const deleteTemplateMutation = useMutation(
    async (templateId) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.delete(`/api/email-templates/${templateId}`, { headers });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Email template deleted successfully');
        queryClient.invalidateQueries(['emailTemplates', currentWorkspace?.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete email template');
      }
    }
  );

  // Duplicate template mutation
  const duplicateTemplateMutation = useMutation(
    async (templateId) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post(`/api/email-templates/${templateId}/duplicate`, {}, { headers });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Email template duplicated successfully');
        queryClient.invalidateQueries(['emailTemplates', currentWorkspace?.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to duplicate email template');
      }
    }
  );

  // Helper function to check if content is HTML
  const isHTMLContent = (content) => {
    if (!content) return false;
    // Check for common HTML tags or patterns
    const htmlPattern = /<[a-z][\s\S]*>/i;
    return htmlPattern.test(content);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
  };

  const handleCopyTemplate = (templateId) => {
    duplicateTemplateMutation.mutate(templateId);
  };

  const handleDeleteTemplate = (templateId) => {
    if (confirmDelete('Are you sure you want to delete this email template?')) {
      deleteTemplateMutation.mutate(templateId);
    }
  };

  const handleEditTemplate = (template) => {
    // Navigate to template creator with edit data
    navigate('/template-creator', {
      state: {
        templateType: template.type || 'text',
        templateData: {
          name: template.name,
          subject: template.subject,
          content: template.content,
          template_type: template.type || 'text'
        },
        editingTemplateId: template.id
      }
    });
  };


  return (
    <div className="bg-gray-50 -m-4 sm:-m-6 lg:-m-8 p-4 sm:p-6 lg:p-8 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Email Templates</h1>
            <p className="text-gray-600 mt-2">Create and manage your email marketing templates</p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedTemplates.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedTemplates.length} selected
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              </div>
            )}
            <button
              onClick={handleNewTemplate}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
            >
              <Plus className="h-4 w-4" />
              <span>New Template</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search email templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Quick Templates Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Create Templates</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickTemplates.map((template, index) => (
            <button
              key={index}
              onClick={() => handleQuickTemplate(template)}
              className="group p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all duration-200 text-center"
            >
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform duration-200">
                {template.icon}
              </div>
              <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                {template.name}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {template.type.toUpperCase()}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Templates Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6 pt-0">
        {loading ? (
          <EmailTemplatesSkeleton />
        ) : templates.length === 0 ? (
          <div className="p-16 text-center">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full h-24 w-24 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-12 w-12 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No email templates found</h3>
            <p className="text-gray-600 mb-6">Create your first email template to get started with email marketing.</p>
            <div className="flex items-center gap-3 justify-center">
              <button
                onClick={handleFixTemplateTypes}
                disabled={fixingTemplateTypes}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2.5 rounded-lg shadow-md hover:shadow-lg hover:from-orange-600 hover:to-red-600 transition-all font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Fix incorrect template types for existing templates"
              >
                {fixingTemplateTypes ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                {fixingTemplateTypes ? 'Fixing...' : 'Fix Types'}
              </button>
              <button
                onClick={handleNewTemplate}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
              >
                <Plus className="h-5 w-5" />
                <span>Create Template</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedTemplates.length === templates.length && templates.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templates.map((template) => (
                  <tr key={template.id} className={`hover:bg-gray-50 transition-colors ${selectedTemplates.includes(template.id) ? 'bg-blue-50' : ''
                    }`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedTemplates.includes(template.id)}
                        onChange={() => handleSelectTemplate(template.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                            {template.type === 'html' ? (
                              <Code className="h-5 w-5 text-blue-600" />
                            ) : template.ai_generated ? (
                              <Sparkles className="h-5 w-5 text-purple-600" />
                            ) : (
                              <FileText className="h-5 w-5 text-green-600" />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {template.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {template.type === 'html' ? 'HTML Template' : template.content.substring(0, 50)}
                            {template.content.length > 50 && '...'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 truncate max-w-xs">
                        {template.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${template.type === 'html'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                          }`}>
                          {template.type || 'text'}
                        </span>
                        {template.ai_generated && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            AI
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${template.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                        }`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(template.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePreviewTemplate(template)}
                          className="text-purple-600 hover:text-purple-900 transition-colors"
                          title="Preview Template"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                          title="Edit Template"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleCopyTemplate(template.id)}
                          className="text-green-600 hover:text-green-900 transition-colors"
                          title="Duplicate Template"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete Template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Template Modal - Enhanced */}
      {showCreateModal && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[100] p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto transform transition-all">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl sm:rounded-t-2xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <Plus className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">
                    {editingTemplateId ? 'Edit Template' : (createType === 'ai' ? 'Create AI Template' : 'Create New Template')}
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTemplateId(null);
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {/* Template Type Selection - Enhanced (Only show when creating new template) */}
              {!editingTemplateId && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Choose Template Type</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      onClick={() => setCreateType('text')}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${createType === 'text'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className="p-2 rounded-lg bg-green-100">
                          <FileText className="h-6 w-6 text-green-600" />
                        </div>
                        <span className="font-medium">Text Template</span>
                        <p className="text-xs text-gray-500 text-center">Simple text-based emails</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setCreateType('html')}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${createType === 'html'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <Code className="h-6 w-6 text-blue-600" />
                        </div>
                        <span className="font-medium">HTML Template</span>
                        <p className="text-xs text-gray-500 text-center">Rich formatted emails</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setCreateType('ai')}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${createType === 'ai'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <Sparkles className="h-6 w-6 text-purple-600" />
                        </div>
                        <span className="font-medium">AI Generated</span>
                        <p className="text-xs text-gray-500 text-center">AI-powered templates</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter template name"
                  />
                </div>

                {createType === 'ai' ? (
                  <>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                        <span className="text-sm font-medium text-purple-800">AI-Powered Template Creation</span>
                      </div>
                      <p className="text-sm text-purple-600 mb-3">
                        Describe what you want and AI will create a professional email template for you instantly!
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Describe Your Email Template</label>
                      <textarea
                        value={formData.prompt}
                        onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all resize-none"
                        rows={editingTemplateId ? 6 : 4}
                        placeholder="E.g., 'Create a welcome email for new customers with a discount offer and company introduction'"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Tip: Be specific about the purpose, audience, and tone you want
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Output Format</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setFormData({ ...formData, template_type: 'text' })}
                          className={`p-3 rounded-lg border-2 transition-all ${formData.template_type === 'text'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="flex items-center space-x-2">
                            <FileText className="h-5 w-5" />
                            <span className="font-medium">Text Format</span>
                          </div>
                        </button>
                        <button
                          onClick={() => setFormData({ ...formData, template_type: 'html' })}
                          className={`p-3 rounded-lg border-2 transition-all ${formData.template_type === 'html'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="flex items-center space-x-2">
                            <Code className="h-5 w-5" />
                            <span className="font-medium">HTML Format</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter email subject"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {createType === 'html' ? 'HTML Content' : 'Content'}
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={editingTemplateId ? 12 : 6}
                        placeholder={createType === 'html' ? 'Enter HTML content...' : 'Enter email content...'}
                      />
                    </div>
                  </>
                )}

              </div>

              {/* Action Buttons - Enhanced */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  {createType === 'ai' && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      <span>AI will generate your template instantly</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTemplateId(null);
                    }}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTemplate}
                    disabled={createTemplateMutation.isLoading}
                    className={`px-6 py-2.5 rounded-lg font-medium shadow-md hover:shadow-lg transition-all flex items-center gap-2 ${createType === 'ai'
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                      } disabled:opacity-50`}
                  >
                    {createType === 'ai' ? (
                      <>
                        <Sparkles className="h-4 w-4" />
                        <span>{createTemplateMutation.isLoading ? (editingTemplateId ? 'Updating...' : 'Generating with AI...') : (editingTemplateId ? 'Update with AI' : 'Generate with AI')}</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>{createTemplateMutation.isLoading ? (editingTemplateId ? 'Updating...' : 'Creating...') : (editingTemplateId ? 'Update Template' : 'Create Template')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Preview Template Modal - Responsive Design */}
      {showPreviewModal && previewTemplate && (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[100] p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden transform transition-all">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-xl sm:rounded-t-2xl p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                    Preview: {previewTemplate.name}
                  </h2>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col lg:flex-row h-full max-h-[calc(95vh-80px)]">
              {/* Template Details - Mobile First */}
              <div className="lg:w-1/3 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200 p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Details</h3>
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-700">Name:</span>
                    </div>
                    <p className="text-sm text-gray-600 break-words">{previewTemplate.name}</p>
                  </div>

                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-700">Subject:</span>
                    </div>
                    <p className="text-sm text-gray-600 break-words">{previewTemplate.subject}</p>
                  </div>

                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-700">Type:</span>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {(previewTemplate.type || 'text').toUpperCase()}
                    </span>
                  </div>

                  {previewTemplate.ai_generated && (
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-gray-700">Generated by:</span>
                      </div>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        AI Powered
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Content - Responsive */}
              <div className="lg:w-2/3 flex flex-col p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Content</h3>
                <div className="flex-1 overflow-auto">
                  <div className="bg-white border rounded-lg p-4 sm:p-6 min-h-[300px] shadow-sm">
                    {previewTemplate.type === 'html' || isHTMLContent(previewTemplate.content) ? (
                      <div
                        className="text-sm text-gray-800 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: previewTemplate.content }}
                      />
                    ) : (
                      <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
                        {previewTemplate.content}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="px-4 py-2 sm:px-6 sm:py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default EmailTemplates;